import React, { useContext, useEffect, useState } from 'react';
import { AlertCircle, Download, FileSpreadsheet, Loader2 } from 'lucide-react';

import { productionApi as api } from 'api/production';
import { warehouseApi } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { UploadCSVAndReviewComponent } from 'shared';

function LSMCell({ column, row }) {
    const currentValue = row.original.currentSlot;
    if (currentValue && currentValue !== row.original.slot) {
        return (
            <div className="space-y-1">
                <Badge variant="destructive" className="line-through">
                    {currentValue}
                </Badge>
                <Badge variant="default" className="bg-green-100 text-green-800">
                    {row.original.slot}
                </Badge>
            </div>
        );
    }
    if (!currentValue) {
        return (
            <Badge variant="default" className="bg-green-100 text-green-800">
                {row.original.slot}
            </Badge>
        );
    }
    return <span className="text-gray-900">{row.original.slot}</span>;
}

function LineSlotMapUpload({ onSuccess, packGroupID }) {
    const [showDummySlotsButton, setShowDummySlotsButton] = useState(false);
    const [loading, setLoading] = useState({
        template: false,
        dummyData: false,
        frozen: false,
    });
    const [error, setError] = useState(null);
    const user = useContext(UserContext);

    useEffect(() => {
        async function getFacilities() {
            const res = await warehouseApi.getFacilities(true);
            if (!res.status || !res.data) {
                return;
            }
            const facility = res.data.rows.find((item) => item.id === user.getFacilityID());
            if (facility) {
                setShowDummySlotsButton(facility.printBatchelorPDF);
            }
        }
        getFacilities();
    }, []);

    const columns = [
        { Header: 'Row', accessor: 'rowNumber' },
        { Header: 'Sellable Item ID', accessor: 'sellableItemID' },
        { Header: 'External sku', accessor: 'externalSku' },
        { Header: 'Name', accessor: 'name' },
        { Header: 'Slot', accessor: 'slot', Cell: LSMCell },
        { Header: 'Errors', accessor: 'errors' },
        { Header: 'Warnings', accessor: 'warnings' },
    ];

    const confirmUpload = async (key) => {
        const res = await api.setLineSlotMap(key);
        return res;
    };

    const upload = async (formData) => {
        const res = await api.uploadLineSlotMap(packGroupID, formData);
        return res;
    };

    const downloadTemplate = async () => {
        setLoading((prev) => ({ ...prev, template: true }));
        setError(null);
        try {
            const result = await api.downloadLineSlotMapCSV(packGroupID);
            if (result && !result.status) {
                setError(result.error || 'Failed to download CSV template');
            }
        } catch (err) {
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading((prev) => ({ ...prev, template: false }));
        }
    };

    const downloadTemplateWithDummyData = async () => {
        setLoading((prev) => ({ ...prev, dummyData: true }));
        setError(null);
        try {
            const result = await api.downloadLineSlotMapCSV(packGroupID, true);
            if (result && !result.status) {
                setError(result.error || 'Failed to download CSV with dummy slots');
            }
        } catch (err) {
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading((prev) => ({ ...prev, dummyData: false }));
        }
    };

    const downloadFrozenCSV = async () => {
        setLoading((prev) => ({ ...prev, frozen: true }));
        setError(null);
        try {
            const result = await api.downloadFrozenCSV(packGroupID);
            if (result && !result.status) {
                setError(result.error || 'Failed to download frozen CSV');
            }
        } catch (err) {
            setError(err.message || 'An unexpected error occurred');
        } finally {
            setLoading((prev) => ({ ...prev, frozen: false }));
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-5 w-5" />
                        Line Slot Map Upload
                    </CardTitle>
                    <CardDescription>
                        Upload and manage line slot mappings for your pack group. Warnings are non-blocking and will
                        allow upload confirmation.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                    <div className="mb-4 flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            onClick={downloadTemplate}
                            disabled={loading.template}
                            className="flex items-center gap-2"
                        >
                            {loading.template ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Download className="h-4 w-4" />
                            )}
                            Download CSV Template
                        </Button>
                        {showDummySlotsButton && (
                            <Button
                                variant="outline"
                                onClick={downloadTemplateWithDummyData}
                                disabled={loading.dummyData}
                                className="flex items-center gap-2"
                            >
                                {loading.dummyData ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Download className="h-4 w-4" />
                                )}
                                Download CSV with Dummy Slots
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            onClick={downloadFrozenCSV}
                            disabled={loading.frozen}
                            className="flex items-center gap-2"
                        >
                            {loading.frozen ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Download className="h-4 w-4" />
                            )}
                            Download Frozen CSV
                        </Button>
                    </div>
                    <UploadCSVAndReviewComponent
                        title=""
                        fileLabel="Line Slot Map File"
                        columns={columns}
                        upload={upload}
                        confirm={confirmUpload}
                        onSuccess={onSuccess}
                    />
                </CardContent>
            </Card>
        </div>
    );
}

export { LineSlotMapUpload };
